/**
 * 角色实体类型定义
 *
 * 定义角色的游戏机制相关状态（HP、技能、装备等）
 * 这些信息会在游戏过程中频繁变化
 */

import { z } from 'zod';
import { normalizeBuildTemplateId, normalizeIdentityTemplateId } from '@/IsekaiRPG/modules/character/characterSemantic';
import { CharacterProfileSchema } from './characterProfile';
import {
  CharacterPresenceSchema,
  DEFAULT_CHARACTER_PRESENCE,
  parseCharacterPresence,
  type CharacterPresence,
} from './characterPresence';
import { CharacterSkillSchema, StatusEffectSchema } from '@/IsekaiRPG/modules/skill/skillTypes';

// ============================================
// 装备槽位
// ============================================

/**
 * 装备槽位
 */
export const EquipmentSlotsSchema = z.object({
  weapon: z.string().optional(),
  armor: z.string().optional(),
  helmet: z.string().optional(),
  accessory1: z.string().optional(),
  accessory2: z.string().optional(),
});

export type EquipmentSlots = z.infer<typeof EquipmentSlotsSchema>;

// ============================================
// 角色基础属性
// ============================================

/**
 * 六维基础属性
 */
export const AttributesSchema = z.object({
  strength: z.number().min(1).default(10).describe('力量：物理伤害与破坏行为'),
  agility: z.number().min(1).default(10).describe('敏捷：速度与精细操作'),
  vitality: z.number().min(1).default(10).describe('体质：生命与防护'),
  intellect: z.number().min(1).default(10).describe('智力：魔法与知识'),
  charisma: z.number().min(1).default(10).describe('魅力：交涉与社交影响'),
  luck: z.number().min(1).default(10).describe('幸运：概率与奇迹'),
});

export type Attributes = z.infer<typeof AttributesSchema>;

// ============================================
// 角色实体
// ============================================

/**
 * 角色实体 (Entity)
 *
 * 包含角色的游戏机制状态
 * 这些信息会在战斗、探索等过程中变化
 */
export const CharacterEntitySchema = z.object({
  // 内部构筑模板 ID（受控、有限、内建）
  buildTemplateId: z.preprocess(
    value => normalizeBuildTemplateId(typeof value === 'string' ? value : undefined),
    z.string(),
  ).default(''),
  // 社会/建筑互动模板 ID（受控、有限、内建）
  identityTemplateId: z.preprocess(
    value => normalizeIdentityTemplateId(typeof value === 'string' ? value : undefined),
    z.string(),
  ).default(''),

  // 六维基础属性（与技能系统解耦）
  attributes: AttributesSchema.default({
    strength: 10,
    agility: 10,
    vitality: 10,
    intellect: 10,
    charisma: 10,
    luck: 10,
  }),

  // 核心生存资源
  hp: z.number().min(0).default(100),
  maxHp: z.number().min(1).default(100),

  // 技能列表
  skills: z.record(z.string(), CharacterSkillSchema).default({}),

  // 装备槽位 (instanceId)
  equipment: EquipmentSlotsSchema.default({}),

  // 状态
  isAlive: z.boolean().default(true),
  statusEffects: z.array(StatusEffectSchema).default([]),
});

export type CharacterEntity = z.infer<typeof CharacterEntitySchema>;

// ============================================
// 完整角色（组合 Profile + Entity）
// ============================================

/**
 * 完整角色
 *
 * 组合角色设定和游戏实体
 */
const CharacterSchemaBase = z.object({
  /** 角色唯一 ID */
  id: z.string(),

  /** 来源角色种子 ID */
  seedId: z.string().optional(),

  /** 角色自身的世界书 ID，仅独立个体角色需要 */
  worldBookId: z.string().optional(),

  /** 角色设定（身份、背景、性格） */
  profile: CharacterProfileSchema,

  /** 角色实体（HP、技能、装备） */
  entity: CharacterEntitySchema,

  /** 出场范围与场景分布规则 */
  presence: CharacterPresenceSchema.default(DEFAULT_CHARACTER_PRESENCE),
});
export const CharacterSchema = CharacterSchemaBase;

export type Character = z.infer<typeof CharacterSchemaBase>;

// ============================================
// 工厂函数
// ============================================

/**
 * 创建默认角色实体
 */
export function createDefaultEntity(options: Partial<CharacterEntity> = {}): CharacterEntity {
  return CharacterEntitySchema.parse(options);
}

/**
 * 创建完整角色
 */
export function createCharacter(
  id: string,
  profile: z.infer<typeof CharacterProfileSchema>,
  entityOptions: Partial<CharacterEntity> = {},
  worldBookId?: string,
  presenceOptions: Partial<CharacterPresence> = {},
): Character {
  return CharacterSchema.parse({
    id,
    worldBookId: worldBookId ?? id,
    profile,
    entity: createDefaultEntity(entityOptions),
    presence: parseCharacterPresence(presenceOptions),
  });
}
